import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DossierField } from "./DossierField";
import { DossierQuestionRow } from "./DossierQuestionRow";
import {
  hasAnswerValue,
  formatAnswerPreview,
  questionMarkerState,
} from "./question-state";
import { isFieldVisible } from "./field-visibility";
import {
  groupSectionFields,
  type FieldGroupKey,
  type GroupedSectionKey,
} from "./field-groups";
import { isValidDzMobilePhone } from "./phone-validation";
import { TeamMembersPanel } from "./TeamMembersPanel";
import { partitionWarnings, type MemberMutationResult } from "../hooks";
import type { Application, SchemaField, SchemaQuestion } from "../types";

/**
 * Pulled out of the ordinary field list on the Team step only — see the component doc comment's
 * "Team is the one exception" section for why this one field cannot be positioned generically.
 */
const NAMED_MEMBERS_CONSENT_FIELD_ID = "named_members_consent_confirmed";

/**
 * `ApplicationVentureProfile.name` / the shared `Venture.name` — a real, writable field on both
 * the backend data model AND `update_venture()`'s accepted keys (confirmed directly: the v2
 * branch's generic `setattr(profile, field, value)` loop plus its own explicit `if "name" in
 * values` block both handle it), but never part of `schema.applicant.sections[project].fields` —
 * checked against a running round, same diligence as `field-groups.ts`'s own note on that section.
 * So there is no `SchemaField` for it to render generically; this constant IS its definition,
 * the same "Team is the one exception" shape `named_members_consent_confirmed` already is above,
 * just for the Project step instead of Team.
 *
 * ⚠ `is_required` IS COSMETIC ONLY, same as everywhere else in this renderer — it draws the "*",
 * it does not block submission. `application_missing_items` has no check for a blank project name
 * today, so a dossier can still be submitted without one; flagged to Aymen as a real gap, not
 * silently treated as already enforced.
 */
const PROJECT_NAME_FIELD_ID = "name";

/**
 * Literal keys, not templates — `t()` only type-checks against a known key, so
 * `t(\`dossier.groups.${group.key}.title\`)` does not compile however obvious the mapping looks
 * (see `rounds/configuration` `FreezeSummary`'s `AXIS_LABEL_KEYS` for the same pattern).
 */
const GROUP_TITLE_KEYS = {
  eligibility: "dossier.groups.eligibility.title",
  commitment: "dossier.groups.commitment.title",
  journey: "dossier.groups.journey.title",
  context: "dossier.groups.context.title",
  language: "dossier.groups.language.title",
  validation: "dossier.groups.validation.title",
  viability: "dossier.groups.viability.title",
  stage: "dossier.groups.stage.title",
  impact: "dossier.groups.impact.title",
  contact: "dossier.groups.contact.title",
  interview: "dossier.groups.interview.title",
  availability: "dossier.groups.availability.title",
  logistics: "dossier.groups.logistics.title",
} as const satisfies Record<FieldGroupKey, string>;

/**
 * Same literal-key discipline as `GROUP_TITLE_KEYS`, but deliberately PARTIAL: "3. Stade" carries
 * no guidance sentence in the frame, and this is what makes that heading-only. `i18n.local.md`
 * pins `returnEmptyString: false`, so `t(key, { defaultValue: "" })` does NOT reliably resolve to
 * an empty string for an absent key — it printed the raw key on screen instead. Checking
 * membership here first sidesteps that quirk rather than fighting it.
 */
const GROUP_HELP_KEYS = {
  eligibility: "dossier.groups.eligibility.help",
  commitment: "dossier.groups.commitment.help",
  journey: "dossier.groups.journey.help",
  context: "dossier.groups.context.help",
  language: "dossier.groups.language.help",
  validation: "dossier.groups.validation.help",
  viability: "dossier.groups.viability.help",
  impact: "dossier.groups.impact.help",
  logistics: "dossier.groups.logistics.help",
} as const satisfies Partial<Record<FieldGroupKey, string>>;

type GroupHelpKey = keyof typeof GROUP_HELP_KEYS;

function hasGroupHelp(key: FieldGroupKey): key is GroupHelpKey {
  return key in GROUP_HELP_KEYS;
}

/**
 * One step's fields, grouped and rendered — Founder and Project both go through this; Team and
 * Practical will too once their own group config exists.
 *
 * ## Two kinds of question, two endpoints, one screen
 *
 * The applicant meets one list, but it is assembled from two sources with different contracts:
 *
 * - the **fixed dossier fields** (`schema.applicant.sections[key].fields`), saved together by one
 *   PATCH to the section's own endpoint (`onSaveSection`);
 * - the **round's authored questions** (`schema.form.sections[]` placed by `applicant_step`), saved
 *   one at a time by `PUT /answers/{id}/` (`onSaveAnswer`) — the SAME endpoint and shape whichever
 *   step they sit on, which is why this one function serves both.
 *
 * Both go through the SAME autosave queue upstream, so a founder save and a project save can never
 * race each other for `expected_revision` — there is only ever one in flight for the whole dossier.
 *
 * ## The group headings are a MIRROR — see `field-groups.ts`
 *
 * Each step's design groups its fields under headings with guidance copy that does real work — one
 * of Founder's is required outright by US-E3-05 step 6. None of it is in the payload, so membership
 * is written client-side, per step, with a fallback that renders any unmapped field rather than
 * losing it. The heading copy is ours; the field labels, order and options remain entirely the
 * round's.
 *
 * ## Team is the one exception, and it is a real sub-resource, not a field
 *
 * `named_members_consent_confirmed` IS an ordinary schema field, but it is pulled out of the
 * generic list and rendered manually AFTER `TeamMembersPanel` — the roster sits between "why is
 * this dossier in your name" and "confirm everyone named agrees", and `groupSectionFields` can only
 * order fields the payload actually sent, not a panel that is not a field at all. See
 * `TeamMembersPanel`'s own doc comment for why the roster needs real create/remove actions instead
 * of a value to type into an existing row.
 */
export function DossierSectionStep({
  application,
  sectionKey,
  fieldErrors,
  onSaveSection,
  onSaveAnswer,
  onCreateMember,
  onUpdateMember,
  onDeleteMember,
}: {
  application: Application;
  sectionKey: GroupedSectionKey;
  /** Field id (or question id) → a translated, hard-error message. See `useDossierAutosave`. */
  fieldErrors: Record<string, string>;
  onSaveSection: (values: Record<string, unknown>) => void;
  onSaveAnswer: (questionId: string, value: unknown) => void;
  /** Only ever called from the Team step — see `TeamMembersPanel`. */
  onCreateMember: (values: {
    name: string;
    role: string;
  }) => Promise<MemberMutationResult>;
  onUpdateMember: (memberId: string, values: Record<string, unknown>) => void;
  onDeleteMember: (memberId: string) => Promise<MemberMutationResult>;
}) {
  const { t } = useTranslation("acquire");

  // The step's TITLE is the chrome's job (`StepIntro`); this component only needs the fields.
  const section = application.schema.applicant.sections.find(
    (item) => item.key === sectionKey
  );
  const authored = application.schema.form.sections.filter(
    (item) => item.applicant_step === sectionKey && item.questions.length > 0
  );

  /**
   * The section's own saved values — `application.founder` for the founder step,
   * `application.project` for project, and so on. All four sections share the same flat,
   * loosely-typed bag shape (`Record<string, unknown>`), which is what lets one component read any
   * of them by key without a per-section schema.
   */
  const savedValues = (application as unknown as Record<string, unknown>)[
    sectionKey
  ] as Record<string, unknown> | undefined;

  /**
   * Local edits, layered over the server's values.
   *
   * A text field cannot be driven straight from the cache: the value would only update once a
   * response came back, so every keystroke would be swallowed until the save landed. Local state
   * holds what is being typed; the server's value is the fallback for anything untouched.
   *
   * Cleared per field on nothing — it is intentionally sticky. The server echoes the same value
   * back, so keeping the local copy avoids a flicker, and a rejected save leaves what was typed on
   * screen rather than reverting it (US-E3-06: *"the answer currently being typed stays on screen
   * and is not discarded"*).
   *
   * Keyed by `sectionKey` in a nested object rather than reset via a `key` prop on the parent: a
   * `key`-forced remount would also throw away in-flight focus and scroll position on every step
   * switch, which a plain state reset does not need to cost.
   */
  const [draftsBySection, setDraftsBySection] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, unknown>>({});

  const drafts = draftsBySection[sectionKey] ?? {};
  const setDrafts = (
    updater: (current: Record<string, unknown>) => Record<string, unknown>
  ) =>
    setDraftsBySection((current) => ({
      ...current,
      [sectionKey]: updater(current[sectionKey] ?? {}),
    }));

  /**
   * Client-side format errors, keyed by field id — checked only at commit (blur), never while
   * still typing, so a half-finished number does not flash red before the applicant is done.
   * Kept separate from the server's `fieldErrors`: a format the backend itself would reject
   * (`PHONE_RE`) is caught here first to save the round trip, but a server error always wins if
   * one is already showing.
   */
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});

  /**
   * Which questions are currently expanded — shared across every step, keyed by field/question id
   * (both are unique across the whole schema), not reset on tab switch for the same reason
   * `draftsBySection` isn't: this component stays mounted the whole time (see the shell's own doc
   * comment), so a Set here behaves exactly like a ref that also happens to re-render.
   */
  const [openQuestionIds, setOpenQuestionIds] = useState<Set<string>>(
    () => new Set()
  );
  const toggleQuestion = (id: string) => {
    setOpenQuestionIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Seeds each section's default-open questions EXACTLY ONCE per visit — "adjusting state when a
   * prop changes", compared during render rather than in an effect (`react-hooks/set-state-in-effect`
   * flags the latter; see `QuestionBankDialog`'s own `wasOpen` pattern in the round builder for the
   * same fix). Runs again below, after this step's groups are known, since the defaults depend on
   * which question in each group is still unanswered.
   */
  const [seededFor, setSeededFor] = useState<string | null>(null);

  /** The value a control should show: what is being typed, else what the server holds. */
  const valueOf = (key: string): unknown =>
    key in drafts ? drafts[key] : savedValues?.[key];

  const answerOf = (questionId: string): unknown => {
    if (questionId in answerDrafts) return answerDrafts[questionId];
    return application.answers.find(
      (answer) => answer.question_id === questionId
    )?.value;
  };

  /** Values as the condition evaluator sees them — drafts included, so a reveal is immediate. */
  const conditionValues = { ...savedValues, ...drafts };

  const { byField, loose } = partitionWarnings(
    application.eligibility_warnings,
    sectionKey
  );

  const isLocked = !application.can_edit;

  const isProject = sectionKey === "project";
  const projectNameField: SchemaField = {
    id: PROJECT_NAME_FIELD_ID,
    section: "project",
    label: t("dossier.project-name.label"),
    help_text: t("dossier.project-name.help"),
    input_type: "SHORT_TEXT",
    is_required: true,
    display_order: -1,
    validation: {},
    conditions: {},
    catalog_category: null,
    data_kind: "CONTEXTUAL",
    options: [],
  };

  const isTeam = sectionKey === "team";
  const fields: SchemaField[] = section?.fields ?? [];
  const consentField = isTeam
    ? fields.find((field) => field.id === NAMED_MEMBERS_CONSENT_FIELD_ID)
    : undefined;
  // Team's grouped-field pass excludes the consent field — it is rendered manually below, after
  // the roster, so its position expresses "confirm everyone JUST LISTED agrees" rather than
  // floating wherever `display_order` happens to place it among the other three team fields.
  /**
   * Any roster add/edit/delete clears `named_members_consent_confirmed` server-side — verified
   * against the backend (`_invalidate_named_member_consents`, called from all three member
   * mutations). The consent checkbox's own local draft does NOT know that: `valueOf` prefers a
   * draft over the server value whenever one exists, and a draft is normally safe to leave sticky
   * (the doc comment on `drafts` explains why) because the server is assumed to echo back
   * whatever was last committed. That assumption breaks here — a DIFFERENT action changes this
   * field's server value without the applicant ever touching the checkbox again, so the checkbox
   * would keep showing "confirmed" after the server silently un-confirmed it. Clearing the draft
   * the moment any member mutation is dispatched (not only on success) is enough: on success
   * `savedValues` is already `false`; on failure nothing changed server-side, so falling back to
   * `savedValues` is still correct.
   */
  const clearConsentDraft = () => {
    setDrafts((current) => {
      if (!(NAMED_MEMBERS_CONSENT_FIELD_ID in current)) return current;
      const updated = { ...current };
      delete updated[NAMED_MEMBERS_CONSENT_FIELD_ID];
      return updated;
    });
  };

  const handleCreateMember: typeof onCreateMember = async (values) => {
    const result = await onCreateMember(values);
    clearConsentDraft();
    return result;
  };
  const handleUpdateMember: typeof onUpdateMember = (memberId, values) => {
    onUpdateMember(memberId, values);
    clearConsentDraft();
  };
  const handleDeleteMember: typeof onDeleteMember = async (memberId) => {
    const result = await onDeleteMember(memberId);
    clearConsentDraft();
    return result;
  };

  const groupableFields = consentField
    ? fields.filter((field) => field.id !== consentField.id)
    : fields;

  const fieldGroups = groupSectionFields(sectionKey, groupableFields).map(
    (group) => ({
      ...group,
      visible: group.fields.filter((field) =>
        isFieldVisible(field.conditions, conditionValues)
      ),
    })
  );

  const contextualQuestions = authored.flatMap((group) =>
    group.questions.filter((question) => question.is_contextual)
  );
  const authoredQuestions = authored.flatMap((group) =>
    group.questions.filter((question) => !question.is_contextual)
  );

  /**
   * The API sends the competency labels with each mapped question. Use the primary mapping as the
   * applicant-facing home for a question: a multi-competency question is still answered once, so
   * rendering it under every mapping would create duplicate controls and duplicate answers.
   */
  const competencyQuestionGroups = (() => {
    const groups = new Map<
      string,
      {
        key: string;
        title: string;
        displayOrder: number;
        questions: SchemaQuestion[];
      }
    >();
    const unassigned: SchemaQuestion[] = [];

    for (const question of authoredQuestions) {
      const competency =
        question.competencies.find((item) => item.is_primary) ??
        question.competencies[0];
      if (!competency) {
        unassigned.push(question);
        continue;
      }
      const key = `competency:${competency.label}`;
      const existing = groups.get(key);
      if (existing) {
        existing.questions.push(question);
      } else {
        groups.set(key, {
          key,
          title: competency.label,
          displayOrder: competency.display_order,
          questions: [question],
        });
      }
    }

    const result = [...groups.values()].sort(
      (a, b) => a.displayOrder - b.displayOrder || a.key.localeCompare(b.key)
    );
    if (unassigned.length > 0) {
      result.push({
        key: "unassigned-questions",
        title: t("dossier.groups.other-questions"),
        displayOrder: Number.MAX_SAFE_INTEGER,
        questions: unassigned,
      });
    }
    return result;
  })();

  const visibleFieldGroups = fieldGroups.filter(
    (group) => group.visible.length > 0
  );
  const contextFieldGroup = visibleFieldGroups.find(
    (group) => group.key === "context"
  );
  const contextIsPresent = Boolean(
    contextFieldGroup || contextualQuestions.length > 0
  );
  const regularFieldGroups = visibleFieldGroups.filter(
    (group) => group.key !== "context" && group.key !== "language"
  );
  const languageFieldGroups = visibleFieldGroups.filter(
    (group) => group.key === "language"
  );
  const regularGroupCount = regularFieldGroups.length;
  const competencyGroupCount = competencyQuestionGroups.length;
  const contextGroupNumber = regularGroupCount + competencyGroupCount + 1;

  // Default-open: one question per rendered section — the first one still unanswered in it — so
  // the step opens looking like Figma's own empty-dossier mockup (679:28260's default state)
  // rather than either fully collapsed or fully expanded. Purely a starting point: every row stays
  // independently clickable regardless of this.
  if (seededFor !== sectionKey) {
    setSeededFor(sectionKey);

    const firstUnansweredField = (candidates: readonly SchemaField[]) =>
      candidates.find((field) => !hasAnswerValue(valueOf(field.id)))?.id;
    const firstUnansweredQuestion = (candidates: readonly SchemaQuestion[]) =>
      candidates.find((question) => !hasAnswerValue(answerOf(question.id)))
        ?.id;

    const defaults = new Set<string>();
    // Not part of any numbered group (see `PROJECT_NAME_FIELD` below), so it sits outside the
    // per-group loops below — a deliberate, narrow exception to "one open by default per group":
    // there is no group for it to belong to, and it is important enough to open unprompted.
    if (sectionKey === "project" && !hasAnswerValue(valueOf(PROJECT_NAME_FIELD_ID))) {
      defaults.add(PROJECT_NAME_FIELD_ID);
    }
    for (const group of regularFieldGroups) {
      const id = firstUnansweredField(group.visible);
      if (id) defaults.add(id);
    }
    for (const group of competencyQuestionGroups) {
      const id = firstUnansweredQuestion(group.questions);
      if (id) defaults.add(id);
    }
    if (contextIsPresent) {
      const fieldId = contextFieldGroup
        ? firstUnansweredField(contextFieldGroup.visible)
        : undefined;
      const questionId = firstUnansweredQuestion(contextualQuestions);
      // Fields render before contextual questions within the group, so the first unanswered ACROSS
      // both, in that order, is the one that opens — never both at once.
      defaults.add(fieldId ?? questionId ?? "");
      defaults.delete("");
    }
    for (const group of languageFieldGroups) {
      const id = firstUnansweredField(group.visible);
      if (id) defaults.add(id);
    }

    if (defaults.size > 0) {
      setOpenQuestionIds((current) => new Set([...current, ...defaults]));
    }
  }

  const renderAuthoredQuestion = (question: SchemaQuestion) => {
    const value = answerOf(question.id);
    const errorMessage = fieldErrors[question.id];
    const hasError = Boolean(errorMessage);
    const isOpen = openQuestionIds.has(question.id);
    const state = questionMarkerState({
      hasError,
      hasWarning: false,
      hasValue: hasAnswerValue(value),
      isOpen,
    });

    return (
      <DossierQuestionRow
        key={question.id}
        label={question.question_text}
        preview={
          isOpen ? null : formatAnswerPreview(value, question.options)
        }
        state={state}
        isOpen={isOpen || hasError}
        isCollapsible={!hasError}
        onToggle={() => toggleQuestion(question.id)}
      >
        <DossierField
          label={question.question_text}
          helpText={question.help_text}
          inputType={question.input_type}
          isRequired={question.is_required}
          options={question.options}
          value={value}
          onChange={(next) =>
            setAnswerDrafts((current) => ({
              ...current,
              [question.id]: next,
            }))
          }
          onCommit={(value) => {
            if (value === undefined) return;
            if (typeof value === "string" && value.trim() === "") return;
            onSaveAnswer(question.id, value);
          }}
          isDisabled={isLocked}
          errorMessage={errorMessage}
        />
      </DossierQuestionRow>
    );
  };

  const renderSchemaField = (field: SchemaField) => {
    const value = valueOf(field.id);
    const warnings = byField.get(field.id) ?? [];
    const errorMessage = fieldErrors[field.id] ?? clientErrors[field.id];
    const hasError = Boolean(errorMessage);
    const isOpen = openQuestionIds.has(field.id);
    const state = questionMarkerState({
      hasError,
      hasWarning: warnings.length > 0,
      hasValue: hasAnswerValue(value),
      isOpen,
    });

    return (
      <DossierQuestionRow
        key={field.id}
        label={field.label}
        preview={isOpen ? null : formatAnswerPreview(value, field.options)}
        state={state}
        isOpen={isOpen || hasError}
        isCollapsible={!hasError}
        onToggle={() => toggleQuestion(field.id)}
      >
        <DossierField
          label={field.label}
          helpText={field.help_text}
          inputType={field.input_type}
          isRequired={field.is_required}
          options={field.options}
          value={value}
          onChange={(next) =>
            setDrafts((current) => ({ ...current, [field.id]: next }))
          }
          onCommit={(value) => {
            // Never send `undefined` — the field was never touched, and PATCH is partial, so
            // omitting it is what "unchanged" means on the wire.
            if (value === undefined) return;

            if (
              field.validation.format === "phone" &&
              typeof value === "string" &&
              value !== "" &&
              !isValidDzMobilePhone(value)
            ) {
              setClientErrors((current) => ({
                ...current,
                [field.id]: t("dossier.errors.invalid-dz-phone"),
              }));
              return;
            }
            if (field.id in clientErrors) {
              setClientErrors((current) => {
                const updated = { ...current };
                delete updated[field.id];
                return updated;
              });
            }
            onSaveSection({ [field.id]: value });
          }}
          isDisabled={isLocked}
          warnings={warnings}
          errorMessage={errorMessage}
        />
      </DossierQuestionRow>
    );
  };

  const renderFieldGroup = (
    group: (typeof visibleFieldGroups)[number],
    sectionNumber: number,
    questions: SchemaQuestion[] = []
  ) => {
    const help =
      group.key !== null && hasGroupHelp(group.key)
        ? t(GROUP_HELP_KEYS[group.key])
        : "";
    return (
      <section
        key={group.key ?? "ungrouped"}
        className="flex flex-col gap-lg border-t border-b border-(--border-default) bg-surface-card px-md py-lg -mx-md sm:-mx-xl sm:px-xl"
      >
        {group.key === null ? null : (
          <div className="flex flex-col gap-xxs">
            <h2 className="text-md-semibold text-(--text-primary)">
              {sectionNumber}. {t(GROUP_TITLE_KEYS[group.key])}
            </h2>
            {help ? (
              <p className="text-sm-regular text-(--text-tertiary)">{help}</p>
            ) : null}
          </div>
        )}
        {group.visible.map(renderSchemaField)}
        {questions.map(renderAuthoredQuestion)}
      </section>
    );
  };

  const renderQuestionGroup = (
    group: (typeof competencyQuestionGroups)[number],
    sectionNumber: number
  ) => (
    <section
      key={group.key}
      className="flex flex-col gap-lg border-t border-b border-(--border-default) bg-surface-card px-md py-lg -mx-md sm:-mx-xl sm:px-xl"
    >
      <div className="flex flex-col gap-xxs">
        <h2 className="text-md-semibold text-(--text-primary)">
          {sectionNumber}. {group.title}
        </h2>
      </div>
      {group.questions.map(renderAuthoredQuestion)}
    </section>
  );

  return (
    <div className="flex flex-col gap-xl">
      {/*
        Warnings that belong to this tab but not to a field we can place. The escape hatch that makes
        the code→field map safe: an unrecognised warning is shown here rather than dropped.
      */}
      {loose.length > 0 ? (
        <div className="flex flex-col gap-xs">
          {loose.map((warning) => (
            <p
              key={warning.code}
              role="status"
              className="rounded-lg border border-(--feedback-warning-ring) bg-(--feedback-warning-ring-outer) p-sm text-sm-regular text-(--text-primary)"
            >
              {warning.message}
            </p>
          ))}
        </div>
      ) : null}

      {/* Not a numbered group — same "ungrouped fallback has no heading" convention every other
          unmapped field already follows in this renderer, since there is no existing Project
          group ("Validation"/"Viabilité"/"Stade"/"Impact") this genuinely belongs under. */}
      {isProject ? (
        <section className="flex flex-col gap-lg border-t border-b border-(--border-default) bg-surface-card px-md py-lg -mx-md sm:-mx-xl sm:px-xl">
          {renderSchemaField(projectNameField)}
        </section>
      ) : null}

      {regularFieldGroups.map((group, index) =>
        renderFieldGroup(group, index + 1)
      )}

      {competencyQuestionGroups.map((group, index) =>
        renderQuestionGroup(group, regularGroupCount + index + 1)
      )}

      {contextIsPresent ? (
        contextFieldGroup ? (
          renderFieldGroup(
            contextFieldGroup,
            contextGroupNumber,
            contextualQuestions
          )
        ) : (
          <section
            key="context-questions"
            className="flex flex-col gap-lg border-t border-b border-(--border-default) bg-surface-card px-md py-lg -mx-md sm:-mx-xl sm:px-xl"
          >
            <div className="flex flex-col gap-xxs">
              <h2 className="text-md-semibold text-(--text-primary)">
                {contextGroupNumber}. {t(GROUP_TITLE_KEYS.context)}
              </h2>
              <p className="text-sm-regular text-(--text-tertiary)">
                {t(GROUP_HELP_KEYS.context)}
              </p>
            </div>
            {contextualQuestions.map(renderAuthoredQuestion)}
          </section>
        )
      ) : null}

      {languageFieldGroups.map((group, index) =>
        renderFieldGroup(
          group,
          contextGroupNumber + (contextIsPresent ? 1 : 0) + index
        )
      )}

      {/*
        The roster is only meaningful once the applicant has said there IS a team — `mode` reads
        from `conditionValues` so a still-unsaved draft toggle reveals it immediately, same as any
        other conditional field on this step.
      */}
      {isTeam && conditionValues.mode === "MULTIPLE" ? (
        <TeamMembersPanel
          members={application.team.members}
          isDisabled={isLocked}
          fieldErrors={fieldErrors}
          onCreateMember={handleCreateMember}
          onUpdateMember={handleUpdateMember}
          onDeleteMember={handleDeleteMember}
        />
      ) : null}

      {consentField &&
      isFieldVisible(consentField.conditions, conditionValues) ? (
        <DossierField
          label={consentField.label}
          helpText={consentField.help_text}
          inputType={consentField.input_type}
          isRequired={consentField.is_required}
          options={consentField.options}
          value={valueOf(consentField.id)}
          onChange={(next) =>
            setDrafts((current) => ({ ...current, [consentField.id]: next }))
          }
          onCommit={(value) => {
            if (value === undefined) return;
            onSaveSection({ [consentField.id]: value });
          }}
          isDisabled={isLocked}
          warnings={byField.get(consentField.id)}
          errorMessage={fieldErrors[consentField.id]}
        />
      ) : null}

      {/*
        US-E3-13: the applicant may answer in whatever language they think in, and saying so is what
        stops people writing worse French than their actual thinking. Shown on every step that
        collects free text, not folded into a group heading — it applies to every written answer.
      */}
      <p className="rounded-lg bg-surface-page p-md text-sm-regular text-(--text-secondary)">
        {t("dossier.step.language-note")}
      </p>
    </div>
  );
}
